// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


// ─────────────────────────────────────────────────────────────────────────────
// Firm-wide Trust Accounting page.
//
// Shows the total trust liability across all clients plus a breakdown by
// client. Clicking a row drills down to that client's detail page on the
// Billing tab, where the per-client register lives.
//
// This page is intentionally read-only — recording deposits/withdrawals
// happens in the per-client view so the workflow stays close to the matter.
// ─────────────────────────────────────────────────────────────────────────────

const th = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontFamily: 'Arial, sans-serif',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#fff',
  fontWeight: 600,
  background: '#0F1838'
};
const td = {
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'Arial, sans-serif',
  borderBottom: '1px solid #eee',
  verticalAlign: 'top'
};

const MOBILE_STYLES = `
@media (max-width: 600px) {
  .trust-desktop { display: none; }
  .trust-mobile  { display: block; }
}
@media (min-width: 601px) {
  .trust-desktop { display: block; }
  .trust-mobile  { display: none; }
}
`;

function fmtMoney(n) {
  const num = parseFloat(n) || 0;
  const sign = num < 0 ? '-' : '';
  return sign + '$' + Math.abs(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtIsoDate(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return iso;
  const [, yr, mo, dy] = m;
  return `${MONTHS[parseInt(mo, 10) - 1]} ${parseInt(dy, 10)}, ${yr}`;
}

export default function AdminTrustPage() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeZero, setIncludeZero] = useState(false);

  useEffect(() => {load(); /* eslint-disable-next-line */}, [includeZero]);

  async function load() {
    setLoading(true);setError('');
    try {
      const params = includeZero ? { include: 'all' } : {};
      const res = await adminApi.getTrustFirmSummary(params);
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <style>{MOBILE_STYLES}</style>
      <h1 className="page-title">Trust Accounting</h1>
      <p className="page-subtitle">
        Firm-wide view of client funds held in trust (IOLTA). Click any client
        to view their full ledger, record a deposit, or post a withdrawal.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Summary card */}
      <div className="card" style={{ marginBottom: 20, padding: 20 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
              Total Trust Liability
            </div>
            <div style={{ fontSize: 32, fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#1a7a5e' }}>
              {loading ? '…' : fmtMoney(data?.firm_balance || 0)}
            </div>
            <div style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginTop: 4 }}>
              {loading ? '' : `Across ${data?.clients?.length || 0} client${(data?.clients?.length || 0) === 1 ? '' : 's'}`}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} />
              Show zero-balance clients
            </label>
          </div>
        </div>
      </div>

      <div className="section-head">Client Balances</div>

      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading…</div>}

      {!loading && data && data.clients.length === 0 &&
      <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 24 }}>
          No client trust balances on file.
          {!includeZero && <div style={{ marginTop: 6, fontSize: 12 }}>Toggle "Show zero-balance clients" to see clients with no current balance.</div>}
        </div>
      }

      {!loading && data && data.clients.length > 0 &&
      <>
          {/* Desktop table */}
          <div className="trust-desktop">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Client</th>
                    <th style={th}>Matter</th>
                    <th style={{ ...th, textAlign: 'right' }}>Trust Balance</th>
                    <th style={{ ...th, textAlign: 'right' }}>Transactions</th>
                    <th style={th}>Last Activity</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.clients.map((c, i) => {
                  const bal = parseFloat(c.balance) || 0;
                  const balColor = bal > 0 ? '#1a7a5e' : bal < 0 ? '#B85042' : '#888';
                  return (
                    <tr key={c.client_id}
                    onClick={() => navigate(`/admin/clients/${c.client_id}?tab=billing`)}
                    style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...td, fontWeight: 500 }}>{c.client_name}</td>
                        <td style={{ ...td, color: '#555' }}>{c.clio_matter_number || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: balColor }}>
                          {fmtMoney(bal)}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#555' }}>
                          {c.txn_count}
                        </td>
                        <td style={{ ...td, color: '#555' }}>{fmtIsoDate(c.last_txn_date)}</td>
                        <td style={td}>
                          <button className="btn btn-sm" onClick={(e) => {e.stopPropagation();navigate(`/admin/clients/${c.client_id}?tab=billing`);}}>
                            Open Register →
                          </button>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="trust-mobile">
            {data.clients.map((c) => {
            const bal = parseFloat(c.balance) || 0;
            const balColor = bal > 0 ? '#1a7a5e' : bal < 0 ? '#B85042' : '#888';
            return (
              <div key={c.client_id} className="card" style={{ padding: 14, marginBottom: 10 }}
              onClick={() => navigate(`/admin/clients/${c.client_id}?tab=billing`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, fontFamily: 'Arial, sans-serif', marginBottom: 4 }}>
                        {c.client_name}
                      </div>
                      <div style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 4 }}>
                        {c.clio_matter_number || '—'} &nbsp;·&nbsp; {c.txn_count} txn{c.txn_count === 1 ? '' : 's'} &nbsp;·&nbsp; last {fmtIsoDate(c.last_txn_date)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: balColor }}>
                        {fmtMoney(bal)}
                      </div>
                    </div>
                  </div>
                </div>);

          })}
          </div>
        </>
      }

      <p style={{ marginTop: 24, fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>
        Trust funds shown here are client property held in the firm's IOLTA account. To record a deposit,
        withdrawal, or transfer, click a client and use the trust register on their Billing tab.
      </p>
    </div>);

}